import { Button } from 'antd';
import React from 'react';
import FormRecaptcha from '../../../FormRecaptcha';
import globalGetPadding from '../../utils/getPadding';
import { getBorderRadiusValue } from '../../utils/borderRadius';

function getFontFamily(fontFamily) {
    switch (fontFamily) {
        case 'MODERN_SANS':
            return '"Helvetica Neue", "Arial Nova", "Nimbus Sans", Arial, sans-serif';
        case 'BOOK_SANS':
            return 'Optima, Candara, "Noto Sans", source-sans-pro, sans-serif';
        case 'ORGANIC_SANS':
            return 'Seravek, "Gill Sans Nova", Ubuntu, Calibri, "DejaVu Sans", source-sans-pro, sans-serif';
        case 'GEOMETRIC_SANS':
            return 'Avenir, "Avenir Next LT Pro", Montserrat, Corbel, "URW Gothic", source-sans-pro, sans-serif';
        case 'HEAVY_SANS':
            return 'Bahnschrift, "DIN Alternate", "Franklin Gothic Medium", "Nimbus Sans Narrow", sans-serif-condensed, sans-serif';
        case 'ROUNDED_SANS':
            return 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, "Arial Rounded MT Bold", Calibri, source-sans-pro, sans-serif';
        case 'MODERN_SERIF':
            return 'Charter, "Bitstream Charter", "Sitka Text", Cambria, serif';
        case 'BOOK_SERIF':
            return '"Iowan Old Style", "Palatino Linotype", "URW Palladio L", P052, serif';
        case 'MONOSPACE':
            return '"Nimbus Mono PS", "Courier New", "Cutive Mono", monospace';
    }
    return undefined;
}

function getRoundedCorners(props) {
    const buttonStyle = props?.buttonStyle ?? SubmitPropsDefaults.buttonStyle;

    switch (buttonStyle) {
        case 'rectangle':
            return undefined;
        case 'pill':
            return 64;
        case 'rounded':
        default:
            return 4;
    }
}

function getButtonSizePadding(props) {
    const size = props?.size ?? SubmitPropsDefaults.size;
    switch (size) {
        case 'x-small':
            return [4, 8];
        case 'small':
            return [8, 12];
        case 'large':
            return [16, 32];
        case 'medium':
        default:
            return [12, 20];
    }
}

const SubmitPropsDefaults = {
    text: 'Submit',
    borderRadius: 6,
    buttonTextColor: '#FFFFFF',
    backgroundColor: '#999999',
    padding: { top: 16, bottom: 16, left: 24, right: 24 }
};

export function Submit({ style, disableStyle, buttonStyle, formClientWidth, unSubData, recaptcha }) {

    const isMobile = formClientWidth <= 480;
    const text = buttonStyle?.text ?? SubmitPropsDefaults.text;
    const btnWidth = buttonStyle?.width ? buttonStyle?.width + buttonStyle?.widthUnit : 'auto';
    const wrapperWidth = buttonStyle?.width && buttonStyle?.widthUnit === '%' ? '100%' : undefined;
    const buttonTextColor = buttonStyle?.buttonTextColor ?? SubmitPropsDefaults.buttonTextColor;
    const buttonBackgroundColor = buttonStyle?.backgroundColor ?? SubmitPropsDefaults.backgroundColor;

    const wrapperStyle = {
        width: wrapperWidth,
        backgroundColor: style?.backgroundColor ?? undefined,
        textAlign: style?.textAlign ?? undefined,
        padding: globalGetPadding(style?.padding, isMobile),
    };
    const linkStyle = {
        color: buttonTextColor,
        fontSize: buttonStyle?.fontSize ? (isMobile ? buttonStyle?.fontSize/1.1 : buttonStyle?.fontSize) : (isMobile ? 14 : 16),
        fontFamily: getFontFamily(buttonStyle?.fontFamily),
        fontWeight: buttonStyle?.fontWeight ?? 'bold',
        backgroundColor: buttonBackgroundColor,
        borderRadius: getBorderRadiusValue(buttonStyle?.borderRadius ?? SubmitPropsDefaults.borderRadius),
        borderColor: buttonStyle?.borderColor ?? buttonBackgroundColor,
        width: btnWidth,
        padding: globalGetPadding(buttonStyle?.padding ?? SubmitPropsDefaults.padding),
        textDecoration: 'none',
    };
    const disabledStyle = {
        color: disableStyle?.buttonTextColor ?? buttonTextColor,
        fontSize: buttonStyle?.fontSize ? (isMobile ? buttonStyle?.fontSize/1.1 : buttonStyle?.fontSize) : (isMobile ? 14 : 16),
        fontFamily: getFontFamily(buttonStyle?.fontFamily),
        fontWeight: buttonStyle?.fontWeight ?? 'bold',
        backgroundColor: disableStyle?.backgroundColor ?? buttonBackgroundColor,
        borderRadius: getBorderRadiusValue(buttonStyle?.borderRadius ?? SubmitPropsDefaults.borderRadius),
        borderColor: disableStyle?.borderColor ?? buttonBackgroundColor,
        width: btnWidth,
        padding: globalGetPadding(buttonStyle?.padding ?? SubmitPropsDefaults.padding),
        textDecoration: 'none',
    }

    // [reCAPTCHA] Whether the submit button should be blocked.
    //  - When reCAPTCHA is enabled but the Google script is still loading or
    //    failed to load, the button is blocked.
    //  - For a v2 checkbox widget, the button stays blocked until the user
    //    completes the challenge (token ready). v2 invisible and v3 modes
    //    execute on submit, so they are not blocked here.
    //  - When the backend returns a v2 fallback challenge with a checkbox
    //    widget, the same rule applies until the user completes it.
    const recaptchaEnabled = !!recaptcha?.enabled && recaptcha?.mode !== 'off';
    const recaptchaScriptReady = !!recaptcha?.scriptReady;
    const recaptchaScriptError = !!recaptcha?.scriptError;
    const v2InitError = !!recaptcha?.v2InitError;
    const isV2CheckboxPending = !!recaptcha?.showV2Widget
        && recaptcha?.v2Config?.widget === 'checkbox'
        && !recaptcha?.v2TokenReady;
    const isRecaptchaBlocking = recaptchaEnabled && (
        recaptchaScriptError
        || !recaptchaScriptReady
        || v2InitError
        || isV2CheckboxPending
    );

    // Existing disabled logic from unSubData (e.g. no topics selected for the
    // unsubscribe form). The button is visually disabled (disabledStyle) and
    // also receives the `disabled` attribute so it is not clickable.
    const isUnsubDisabled = !!unSubData && !unSubData?.selectTopics?.length;
    const isButtonDisabled = isRecaptchaBlocking || isUnsubDisabled || !!unSubData?.loading;
    const buttonStyleToUse = isButtonDisabled ? disabledStyle : linkStyle;

    return (
        <div style={wrapperStyle}>
            {/* [reCAPTCHA] render the reCAPTCHA widget above the submit button.
                The widget is only mounted when the form schema enables it. */}
            {recaptchaEnabled ? (
                <FormRecaptcha
                    v2Ref={recaptcha?.v2Ref}
                    v2Config={recaptcha?.v2Config}
                    v3Config={recaptcha?.v3Config}
                    showV2Widget={recaptcha?.showV2Widget}
                    showV3Widget={recaptcha?.showV3Widget}
                    onV3Ready={recaptcha?.onV3Ready}
                    onV3Error={recaptcha?.onV3Error}
                    onV2Ready={recaptcha?.onV2Ready}
                    onV2Change={recaptcha?.onV2Change}
                    onV2Expired={recaptcha?.onV2Expired}
                    onV2Errored={recaptcha?.onV2Errored}
                    scriptReady={recaptcha?.scriptReady}
                    scriptError={recaptcha?.scriptError}
                    v2Error={recaptcha?.v2Error}
                    v2Expired={recaptcha?.v2Expired}
                    v2TokenReady={recaptcha?.v2TokenReady}
                    v2InitError={recaptcha?.v2InitError}
                    style={{ marginBottom: '12px' }}
                />
            ) : null}
            {
                unSubData?.result === true ? <Button style={linkStyle} onClick={unSubData?.handleReturn}>
                    Return
                </Button> : <Button
                    loading={unSubData?.loading}
                    style={buttonStyleToUse}
                    htmlType="submit"
                    disabled={isButtonDisabled}
                >
                    {text}
                </Button>
            }
            {
                unSubData?.result !== null ? <p style={{
                    margin: 0,
                    color: unSubData?.result === true ? 'green' : 'red',
                    fontWeight: 500,
                }}>
                    {unSubData?.result === true ? 'Unsubscribe Successful!' : unSubData?.result}
                </p> : null
            }
        </div>
    );
}

